import React, { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import { VariableSizeList as List } from 'react-window';
import { useTranslation } from 'react-i18next';
import { ChevronRightIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import type { CustomerGroup, Customer, Country, Factory, Line, Machine } from '../services/BackendDataService';
import { LineIcon } from './LineIcon';
import { MachineIcon } from './MachineIcon';
import { getCountryFlag } from '../utils/countryFlags';
import {
  translateCountryName, 
  translateMachineType
} from '../utils/sapMetadataTranslations';
import { useThemeClasses } from '../providers/ThemeProvider';
import { useSAPDataStore } from '../store/SAPDataStore';
import { cx } from '@digitalisation/enlixy-comp';

interface SAPTreeViewVirtualizedProps {
  sapData: Record<string, CustomerGroup>;
  onMachineClick?: (machine: Machine, context: { line: Line; factory: Factory; country: Country; customer: Customer }) => void;
  onLineClick?: (line: Line, context: { factory: Factory; country: Country; customer: Customer }) => void;
  onFactoryClick?: (factory: Factory, context: { country: Country; customer: Customer }) => void;
  expandedNodes: Set<string>;
  onToggleNode: (nodeId: string) => void;
  isExpanded: (nodeId: string) => boolean;
  onExpandAll?: () => void;
  onCollapseAll?: () => void;
  height?: number; // Optional height override
  isMobile?: boolean;
}

interface SAPTreeViewVirtualizedProps {
  sapData: Record<string, CustomerGroup>;
  onMachineClick?: (machine: Machine, context: { line: Line; factory: Factory; country: Country; customer: Customer }) => void;
  onLineClick?: (line: Line, context: { factory: Factory; country: Country; customer: Customer }) => void;
  onFactoryClick?: (factory: Factory, context: { country: Country; customer: Customer }) => void;
  expandedNodes: Set<string>;
  onToggleNode: (nodeId: string) => void;
  isExpanded: (nodeId: string) => boolean;
  onExpandAll?: () => void;
  onCollapseAll?: () => void;
  height?: number; // Optional height override
  isMobile?: boolean;
}

// Virtual tree node interface
interface VirtualTreeNode {
  id: string;
  label: string;
  icon: string | React.ReactNode;
  level: number;
  type: 'group' | 'region' | 'country' | 'customer' | 'factory' | 'line' | 'machine';
  count?: number;
  isExpandable: boolean;
  isExpanded: boolean;
  onClick?: () => void;
  data?: any;
  context?: any;
  machine?: Machine; // For machine nodes
}

// Individual row component for virtualization
const VirtualTreeRow = React.memo<{
  index: number;
  style: React.CSSProperties;
  data: {
    nodes: VirtualTreeNode[];
    onToggle: (nodeId: string) => void;
    onMachineClick?: (machine: Machine, context: any) => void;
    theme: any;
    t: any;
    i18n: any;
    showOnlyWithData: boolean;
  };
}>(({ index, style, data }) => {
  const { nodes, onToggle, onMachineClick, theme, t, i18n, showOnlyWithData } = data;
  const node = nodes[index];
  
  // Safety check for node existence
  if (!node) {
    return (
      <div style={style}>
        <div className={`flex items-center p-2 ${theme.textMuted}`}>
          Loading...
        </div>
      </div>
    );
  }
  
  const indentLevel = node.level * 20;
  
  // Machine node rendering
  if (node.type === 'machine' && node.machine) {
    const translatedBautyp = translateMachineType(node.machine.bautyp || node.machine.machineType || '', i18n.language);
    
    return (
      <div style={style}>
        <div 
          className={`flex items-center p-3 cursor-pointer transition-colors ${theme.bgSurface} ${theme.hover} ${theme.borderPrimary} border-l rounded-r-lg min-h-[140px]`}
          style={{ paddingLeft: `${32 + indentLevel}px` }}
          onClick={() => onMachineClick?.(node.machine!, node.context)}
        >
          <div className="mr-3 flex-shrink-0">
            <MachineIcon 
              machineId={node.machine.maschinennummer || node.machine.machineId}
              machineType={node.machine.bautyp || node.machine.machineType}
              size={120}
              className={theme.textInfo}
            />
          </div>
          <span className="flex-1 flex flex-col justify-center">
            <span className={`font-medium ${theme.textPrimary}`}>
              {node.machine.maschinennummer} - {(node.machine.bautyp || node.machine.machineType || translatedBautyp || '').replace(new RegExp(`^${node.machine.maschinennummer}\\s*[-–]\\s*`), '')}
            </span>
          </span>
          {/* Show No Data badge only for machines without data when showing all machines */}
          {showOnlyWithData && !node.machine.datenVerfuegbar && (
            <span className={cx(
              "px-2 py-1 rounded-full text-xs font-medium",
              `${theme.bgError} ${theme.textWhite}`
            )}>
              {t('noData') || 'No Data'}
            </span>
          )}
        </div>
      </div>
    );
  }
  
  // Regular tree node rendering
  return (
    <div style={style}>
      <div 
        className={cx(
          "flex items-center p-2 cursor-pointer transition-colors duration-200 min-h-[60px]",
          theme.bgSecondary,
          theme.borderPrimary,
          node.onClick ? `${theme.textPrimary} ${theme.hover}` : theme.textSecondary
        )}
        style={{ paddingLeft: `${12 + indentLevel}px` }}
        onClick={(e) => {
          e.stopPropagation();
          if (node.onClick) {
            node.onClick();
          } else if (node.isExpandable) {
            onToggle(node.id);
          }
        }}
      >
        {/* Chevron for expandable nodes */}
        {node.isExpandable && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggle(node.id);
            }}
            className={`mr-2 p-1 rounded transition-colors ${theme.hover} hover:${theme.hoverSecondary}`}
          >
            {node.isExpanded ? (
              <ChevronDownIcon className={`w-4 h-4 ${theme.textSecondary}`} />
            ) : (
              <ChevronRightIcon className={`w-4 h-4 ${theme.textSecondary}`} />
            )}
          </button>
        )}
        
        {/* Icon */}
        {node.icon && (
          <div className="mr-2 flex items-center justify-center w-12 h-12">
            {typeof node.icon === 'string' ? (
              <span className="text-lg">{node.icon}</span>
            ) : (
              node.icon
            )}
          </div>
        )}
        
        {/* Label */}
        <span className={cx(
          "flex-1 font-medium",
          theme.textPrimary,
          node.onClick && `hover:${theme.linkPrimary} cursor-pointer`
        )}>
          {node.label}
        </span>
        
        {/* Count badge */}
        {node.count !== undefined && (
          <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${theme.bgTertiary} ${theme.textSecondary}`}>
            {node.count}
          </span>
        )}
      </div>
    </div>
  );
});

VirtualTreeRow.displayName = 'VirtualTreeRow';

export const SAPTreeViewVirtualized: React.FC<SAPTreeViewVirtualizedProps> = ({ 
  sapData, 
  onMachineClick, 
  onLineClick, 
  onFactoryClick,
  expandedNodes,
  onToggleNode,
  isExpanded,
  onExpandAll,
  onCollapseAll,
  height,
  isMobile: _isMobile = false
}) => {
  const { t, i18n } = useTranslation();
  const theme = useThemeClasses();
  const showOnlyWithData = useSAPDataStore(state => state.showOnlyWithData);

  // Ref for the List component to reset cache when needed
  const listRef = useRef<any>(null);
  
  // State for responsive height
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);

  // Update height on window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowHeight(window.innerHeight);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate dynamic height - fills remaining viewport minus header space
  const dynamicHeight = useMemo(() => {
    if (height) return height;
    // Use viewport height minus typical header/navigation space (approximately 200px)
    return Math.max(400, windowHeight - 200);
  }, [height, windowHeight]);

  // Flattened and filtered tree nodes
  const virtualNodes = useMemo(() => {
    const nodes: VirtualTreeNode[] = [];
    
    Object.entries(sapData).forEach(([groupKey, customerGroup]) => {
      const groupId = `group-${groupKey}`;
      const groupExpanded = isExpanded(groupId);
      
      nodes.push({
        id: groupId,
        label: customerGroup.kundengruppe,
        icon: '🏢',
        level: 0,
        type: 'group',
        count: customerGroup.regionen?.length || 0,
        isExpandable: true,
        isExpanded: groupExpanded
      });
      
      if (groupExpanded && customerGroup.regionen) {
        customerGroup.regionen.forEach((region) => {
          const regionId = `region-${groupKey}-${region.region}`;
          const regionExpanded = isExpanded(regionId);
          
          nodes.push({
            id: regionId,
            label: region.region,
            icon: '🌍',
            level: 1,
            type: 'region',
            count: region.laender?.length || 0,
            isExpandable: true,
            isExpanded: regionExpanded
          });
          
          if (regionExpanded && region.laender) {
            region.laender.forEach((country) => {
              const countryId = `country-${groupKey}-${region.region}-${country.land}`;
              const countryExpanded = isExpanded(countryId);
              
              nodes.push({
                id: countryId,
                label: translateCountryName(country.land, i18n.language),
                icon: getCountryFlag(country.land),
                level: 2,
                type: 'country',
                count: country.kunden?.length || 0,
                isExpandable: true,
                isExpanded: countryExpanded
              });
              
              if (countryExpanded && country.kunden) {
                country.kunden.forEach((customer) => {
                  const customerId = `customer-${groupKey}-${region.region}-${country.land}-${customer.kundennummer}`;
                  const customerExpanded = isExpanded(customerId);
                  
                  nodes.push({
                    id: customerId,
                    label: customer.kundenName,
                    icon: '👥',
                    level: 3,
                    type: 'customer',
                    count: customer.werke?.length || 0,
                    isExpandable: true,
                    isExpanded: customerExpanded
                  });
                  
                  if (customerExpanded && customer.werke) {
                    customer.werke.forEach((factory) => {
                      const factoryId = `factory-${customer.kundennummer}-${factory.werkName}`;
                      const factoryExpanded = isExpanded(factoryId);
                      
                      nodes.push({
                        id: factoryId,
                        label: factory.werkName,
                        icon: '🏭',
                        level: 4,
                        type: 'factory',
                        count: factory.linien?.length || 0,
                        isExpandable: true,
                        isExpanded: factoryExpanded,
                        onClick: () => {
                          // First expand/collapse the factory node
                          onToggleNode(factoryId);
                          // Then call the factory click handler
                          onFactoryClick?.(factory, { country, customer });
                        },
                        data: factory,
                        context: { country, customer }
                      });
                      
                      if (factoryExpanded && factory.linien) {
                        factory.linien.forEach((line) => {
                          const lineId = `line-${factory.werkName}-${line.linieBK}`;
                          const lineExpanded = isExpanded(lineId);
                          
                          // OEM number is on the line (not on machines)
                          const oemSuffix = line.oemNummer ? ` (OEM: ${line.oemNummer})` : '';
                          
                          nodes.push({
                            id: lineId,
                            label: (line.linieNummer && line.linieNummer !== 'undefined' ? `${line.linieNummer} - ${line.linieName.replace(new RegExp(`^${line.linieNummer}\\s*[-–]?\\s*`), '')}` : line.linieName) + oemSuffix,
                            icon: <LineIcon machineCount={line.totalMachineCount || line.maschinen?.length || 0} size={96} className={theme.textPrimary} />,
                            level: 5,
                            type: 'line',
                            count: line.maschinen?.length || 0,
                            isExpandable: true,
                            isExpanded: lineExpanded,
                            onClick: () => {
                              // For lines, clicking should navigate (not expand)
                              onLineClick?.(line, { factory, country, customer });
                            },
                            data: line,
                            context: { factory, country, customer }
                          });
                          
                          if (lineExpanded && line.maschinen) {
                            line.maschinen.forEach((machine) => {
                              nodes.push({
                                id: `machine-${factory.werkName}-${line.linieBK}-${machine.maschinennummer}`,
                                label: machine.machineName || machine.maschinennummer || '',
                                icon: '',
                                level: 6,
                                type: 'machine',
                                isExpandable: false,
                                isExpanded: false,
                                machine,
                                context: { line, factory, country, customer }
                              });
                            });
                          }
                        });
                      }
                    });
                  }
                });
              }
            });
          }
        });
      }
    });
    
    return nodes;
  }, [sapData, expandedNodes, isExpanded, i18n.language, theme.textPrimary, onFactoryClick, onLineClick]);

  // Debounce tree updates to prevent rapid re-renders
  const stableVirtualNodes = useMemo(() => virtualNodes, [virtualNodes.length, expandedNodes.size]);

  // Calculate item size based on node type - memoized for better performance
  const getItemSize = useCallback((index: number) => {
    const node = stableVirtualNodes[index];
    if (!node) return 60; // Fallback height
    
    if (node.type === 'machine') {
      return 140; // Height for machine rows (120px icon + padding)
    }
    return 60; // Height for regular tree nodes
  }, [stableVirtualNodes]);

  // Reset List cache when virtualNodes change significantly
  useEffect(() => {
    if (listRef.current) {
      // Reset the entire cache when tree structure changes
      listRef.current.resetAfterIndex(0, true);
    }
  }, [stableVirtualNodes.length]); // Only reset when the total count changes

  // Optimized handlers
  const handleExpandAll = useCallback(() => {
    if (onExpandAll) {
      onExpandAll();
    }
  }, [onExpandAll]);
  
  const handleCollapseAll = useCallback(() => {
    if (onCollapseAll) {
      onCollapseAll();
    }
  }, [onCollapseAll]);

  if (Object.keys(sapData).length === 0) {
    return (
      <div className={`flex flex-col items-center justify-center p-8 text-center ${theme.bgSecondary} rounded-lg`}>
        <div className="text-6xl mb-4">🌳</div>
        <h3 className={`text-xl font-semibold mb-2 ${theme.textPrimary}`}>{t('noSAPData') || 'No SAP Data Available'}</h3>
        <p className={theme.textSecondary}>{t('loadSAPData') || 'Please load SAP data to see the tree structure.'}</p>
      </div>
    );
  }

  return (
    <div className={`${theme.bgPrimary} rounded-lg overflow-hidden flex flex-col h-full`}>
      {/* Header */}
      <div className={`flex items-center justify-between p-4 ${theme.bgSecondary} ${theme.borderPrimary} border-b flex-shrink-0`}>
        <div className={`text-lg font-semibold ${theme.textPrimary}`}>
          {t('List of available assets') || 'SAP Structure'} 
          <span className={`ml-2 text-sm font-normal ${theme.textSecondary}`}>
            ({stableVirtualNodes.length} items)
          </span>
        </div>
        <div className="flex gap-2">
          <button
            className={`px-3 py-1 text-sm rounded transition-colors ${theme.bgInfo} ${theme.textWhite} hover:opacity-90`}
            onClick={handleExpandAll}
            type="button"
          >
            {t('expandAll') || 'Expand All'}
          </button>
          <button
            className={`px-3 py-1 text-sm rounded transition-colors ${theme.bgSecondary} ${theme.textSecondary} ${theme.borderPrimary} border hover:${theme.hoverSecondary}`}
            onClick={handleCollapseAll}
            type="button"
          >
            {t('collapseAll') || 'Collapse All'}
          </button>
        </div>
      </div>
      
      {/* Tree content - virtualized rendering */}
      <div className="flex-1 overflow-hidden">
        <List
          ref={listRef}
          height={dynamicHeight}
          width="100%"
          itemCount={stableVirtualNodes.length}
          itemSize={getItemSize}
          overscanCount={5} // Render 5 extra items above/below viewport for smoother scrolling
          itemData={{
            nodes: stableVirtualNodes,
            onToggle: onToggleNode,
            onMachineClick,
            theme,
            t,
            i18n,
            showOnlyWithData
          }}
        >
          {VirtualTreeRow}
        </List>
      </div>
      
      {/* Performance statistics */}
      <div className={`p-3 text-xs ${theme.bgSecondary} ${theme.textSecondary} border-t ${theme.borderPrimary} flex-shrink-0`}>
        ⚡ Virtualized rendering: Showing only visible items out of {stableVirtualNodes.length} total (performance optimized)
      </div>
    </div>
  );
};
